// FormatView: the answer area for every format, in the answering, locked and revealed phases.
// Owned by the formats work package; screens only pass props (see formats/types.ts for the contract).
//   mcq      four options: a 2×2 grid of tiles when every option fits a tile (no word breaks in the middle), else rows
//   tf       two big tiles, a check for True and a cross for False
//   gmcq     the graphic (bloods slip, bedside monitor, rhythm strip or 12-lead) between the stem and the options
//   spot     tap the culprit on the bloods slip
//   clues    diagnose in five: dealt clue cards, a points meter, answers tappable from the start
//   number   type the number on a pad, the unit fixed on screen (v2.2)
//   hotspot  tap a lead on the 12-lead or a reading on the bedside monitor (v2.2)
//   order    tap the steps in order (v2.2)
//   multi    pick every right answer, or name one where rarer right answers score more (v2.2)
// Classroom mode (display 'screen'): the first five show the answer marks only; the v2.2 formats keep their controls
// (number keeps the pad, order and multi become compact lists, hotspot becomes one button per region under a small
// graphic). At the reveal, right and wrong are marked and the room's split is shown.
import type { FormatViewProps } from './types';
import { Stem } from '@/components/Stem';
import { GraphicCard } from '@/clinical/GraphicCard';
import { effectivePhase } from './layout';
import { OptionList, pickOf, useAnswerTap } from './parts';
import { CluesView } from './Clues';
import { SpotView } from './Spot';
import { ClassroomMarks } from './Classroom';
import { NumberView } from './Number';
import { HotspotView } from './Hotspot';
import { OrderView } from './Order';
import { MultiView } from './Multi';
import { Split, useWide } from './split';
import './formats.css';

export type { FormatViewProps, AnswerPhase, LiveQuestion, AnswerPayload } from './types';
export { NumberResult, NumberPad } from './Number';
export { OrderResult } from './Order';
export { MultiResult } from './Multi';
export { RegionButtons, HotspotAnswer } from './Hotspot';
export { CreditSplit, CreditBadge, NumberHistogram } from './reveal';

export function FormatView(props: FormatViewProps) {
  const { question: q, display = 'phones' } = props;
  switch (q.format) {
    case 'number': return <NumberView {...props} />;
    case 'hotspot': return <HotspotView {...props} />;
    case 'order': return <OrderView {...props} />;
    case 'multi': return <MultiView {...props} />;
    default: break;
  }
  // The one-pick formats read the pick from myOptionId; take it from myAnswer when that is where it came.
  const p = { ...props, myOptionId: pickOf(props) };
  if (display === 'screen') return <ClassroomMarks {...p} />;
  switch (q.format) {
    case 'tf': return <TrueFalseView {...p} />;
    case 'spot': return q.graphic?.type === 'bloods' ? <SpotView {...p} /> : <ChoiceView {...p} />;
    case 'clues': return <CluesView {...p} />;
    default: return <ChoiceView {...p} />;
  }
}

/** mcq and gmcq: the stem, the graphic when there is one, then the options. */
function ChoiceView(props: FormatViewProps) {
  const { question: q, myOptionId, reveal } = props;
  const phase = effectivePhase(props.phase, myOptionId, reveal);
  const { tap, live } = useAnswerTap({ ...props, phase });
  const wide = useWide();
  return (
    <div className="fx-format" data-format={q.format} data-phase={phase} data-display="phones" data-layout={wide ? 'split' : undefined}>
      <Split wide={wide}
        left={<>
          <Stem text={q.stem} />
          {q.graphic && <GraphicCard graphic={q.graphic} stem={q.stem} revealed={phase === 'revealed'} deadlineAt={phase === 'revealed' ? undefined : q.deadlineAt} enlargeable />}
        </>}
        right={<OptionList options={q.options} format={q.format} phase={phase} myOptionId={myOptionId} reveal={reveal} live={live} onTap={tap} />} />
    </div>
  );
}

function TrueFalseView(props: FormatViewProps) {
  const { question: q, myOptionId, reveal } = props;
  const phase = effectivePhase(props.phase, myOptionId, reveal);
  const { tap, live } = useAnswerTap({ ...props, phase });
  const wide = useWide();
  return (
    <div className="fx-format" data-format="tf" data-phase={phase} data-display="phones" data-layout={wide ? 'split' : undefined}>
      <Split wide={wide}
        left={<>
          <Stem text={q.stem} />
          {q.graphic && <GraphicCard graphic={q.graphic} stem={q.stem} revealed={phase === 'revealed'} deadlineAt={phase === 'revealed' ? undefined : q.deadlineAt} enlargeable />}
        </>}
        right={<OptionList options={q.options} format="tf" phase={phase} myOptionId={myOptionId} reveal={reveal} live={live} onTap={tap} />} />
    </div>
  );
}
